import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../../../hooks/store-hooks";
import {
  AppBar,
  Box,
  Button,
  debounce,
  Dialog,
  Divider,
  Grid,
  Skeleton,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  useProjetosService,
  useRecortesExtracaoService,
} from "../../../../../../use-cases/geo-veles";
import { theme } from "../../../../../styles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { CreateRecorteProjetoForm } from "../create-recorte-projeto-form";

import { RecortesProjetoList } from "../recortes-projeto-list";
import { useLayoutRecorteExtracaoService } from "../../../../../../use-cases/geo-veles/layout-recortes";

export const CreateLayoutRecorteExtracao = () => {
  // State
  const { projetoAberto } = useAppSelector((state) => state.projetos);
  const { openForm, recorteExtracao } = useAppSelector(
    (state) => state.recorteExtracao
  );
  // Use State
  const [loading, setLoading] = useState<boolean>(true);

  const {currentUser} = useAppSelector((state) => state.user);
  const access = useAppSelector((state) => state.user.access);

  // Params
  const { id } = useParams();
  // Hook
  const {
    resetRecorteExtracao,
    openCreateRecorteForm,
    resetCreateRecorteForm,
  } = useRecortesExtracaoService();

  const { setCreateExtracaoFormQuantidade, setVerifyAcesso } =
    useProjetosService();

  const {
    createLayoutRecorteExtracao,
    setCreateLayoutRecorteFormNome,
    resetLayoutSelecionado,
  } = useLayoutRecorteExtracaoService();

  const navigate = useNavigate();
  // Ref to prevent multiple calls
  const hasFetched = useRef(false);
  const [layoutName, setLayoutName] = useState(
    "Layout " + currentUser?.LoginUsuario
  );

  useEffect(() => {
    if (id && !hasFetched.current) {
      setLoading(false);
      hasFetched.current = true;
    }
  }, [id]);

  useEffect(() => {
    if (!access) {
      setVerifyAcesso();
    }
  });

  // Functions
  function closeDialog() {
    navigate(`/geo-veles/projetos/${id}/recorte`);
    resetRecorteExtracao();
    openCreateRecorteForm(false);
    resetLayoutSelecionado();
  }

  const debouncedSetCreateLayoutRecorteFormNome = useCallback(
    debounce((nome: string) => {
      setCreateLayoutRecorteFormNome(nome);
    }, 500),
    []
  );

  const handleChange = (value: string) => {
    setLayoutName(value); // Atualiza o estado local imediatamente
    debouncedSetCreateLayoutRecorteFormNome(value); // Envia o valor debounced para o Redux
  };

  let qtdDigitada: number = 0;

  useEffect(() => {
    if (qtdDigitada > 0) {
      setCreateExtracaoFormQuantidade(String(qtdDigitada));
    }
  });

  useEffect(() => {
    setLayoutName(layoutName); // Atualiza o estado local imediatamente
    debouncedSetCreateLayoutRecorteFormNome(layoutName); // Envia o valor debounced para o Redux
  }, [layoutName, debouncedSetCreateLayoutRecorteFormNome]);

  useEffect(() => {
    if (recorteExtracao) {
      resetRecorteExtracao();
    }
  }, []);

  return (
    <>
      <Dialog open={true} onClose={closeDialog} fullScreen disableEscapeKeyDown>
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography
              sx={{ ml: 2, flex: 1 }}
              textTransform={"uppercase"}
              variant="h6"
              component="div"
            >
              {loading ? (
                <Skeleton animation="wave" variant="text" width={400} />
              ) : (
                <>
                  Criação de Layout dos recortes - Projeto{" "}
                  <strong>{projetoAberto?.Nome ?? ""}</strong>
                </>
              )}
            </Typography>
            <Button
              color="inherit"
              onClick={closeDialog}
              aria-label="close"
              sx={{
                fontWeight: "bold",
              }}
            >
              Fechar
            </Button>
          </Toolbar>
        </AppBar>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          p={2}
        >
          <Grid
            item
            xs={12}
            container
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Grid container item xs={10} alignItems="stretch">
              <Grid item xs={1}>
                <Typography
                  variant="h6"
                  color="primary"
                  fontWeight="bold"
                  textTransform="uppercase"
                  textAlign="center"
                ></Typography>
              </Grid>
            </Grid>
            <Grid item xs={10} mb={2}>
              <Divider variant="middle" color={theme.palette.primary.main} />
            </Grid>
            <Grid item xs={5}>
              <TextField
                //error={layoutName.length === 0} // Mostra o erro se o campo estiver vazio
                helperText={"Insira aqui o nome do Layout"}
                defaultValue={layoutName}
                onChange={debounce((e) => {
                  handleChange(e.target.value);
                }, 500)}
                autoFocus
                id="nomeLayout"
                label="Digite o nome do layout do recorte"
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <Button
                fullWidth
                variant="text"
                endIcon={openForm ? <RemoveIcon /> : <AddIcon />}
                onClick={() => {
                  if (openForm) {
                    resetCreateRecorteForm();
                    openCreateRecorteForm(false);
                    //setRecorteExtracao();
                  } else {
                    openCreateRecorteForm(true);
                  }
                }}
              >
                {openForm ? "Descartar recorte" : "Adicionar novo recorte"}
              </Button>
            </Grid>

            <Grid item xs={10}>
              <Divider variant="middle" color={theme.palette.primary.main} />
            </Grid>

            <Grid item xs={10} display={"flex"} mt={2}>
              {openForm && <CreateRecorteProjetoForm />}
            </Grid>

            <Grid item xs={10}>
              <Typography
                variant="h6"
                color="primary"
                fontWeight="bold"
                textTransform="uppercase"
                textAlign="center"
              >
                Recortes para o layout
              </Typography>
            </Grid>
            <Grid item xs={10} mb={2}>
              <Divider variant="middle" color={theme.palette.primary.main} />
            </Grid>
            <Grid item xs={10} mb={3}>
              {recorteExtracao &&
              Array.isArray(recorteExtracao.Recortes) &&
              recorteExtracao.Recortes.length > 0 ? (
                <RecortesProjetoList
                  temRecorteExtracao={recorteExtracao}
                  modoSomenteLeitura={false}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="bold"
                  textTransform="uppercase"
                  textAlign="center"
                >
                  Nenhum recorte encontrado no momento.
                </Typography>
              )}
            </Grid>
            <Grid item xs={4}>
              <Button
                disabled={
                  recorteExtracao.Recortes.length < 1 ||
                  layoutName.trim() === ""
                }
                variant="contained"
                color="primary"
                fullWidth
                endIcon={<AddIcon />}
                onClick={() => {
                  createLayoutRecorteExtracao();
                }}
              >
                Criar Layout
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Dialog>
    </>
  );
};